import { Box } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useGetBusinesses } from 'apis/agency/hook';
import { BusinessProfileResponse } from 'apis/business/types';
import BusinessesHeader, { BusinessesViewMode } from './components/BusinessesHeader';
import BusinessesListing from './components/BusinessesListing';
import BusinessesInsights from './components/BusinessesInsights';
import AddBusinessTagsDrawer from './components/AddBusinessTagsDrawer';
import BusinessAddonsDrawer from './components/BusinessAddonsDrawer';
import { usePagination } from '@/components/Common/Pagination';

export default function Businesses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode: BusinessesViewMode = searchParams.get('view') === 'insights' ? 'insights' : 'list';
  const [searchText, setSearchText] = useState<string | null>(null);
  const [tagsBusiness, setTagsBusiness] = useState<BusinessProfileResponse | null>(null);
  const [addonsBusiness, setAddonsBusiness] = useState<BusinessProfileResponse | null>(null);
  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();

  const { control, watch } = useForm({
    defaultValues: {
      status: '0',
      tags: [] as string[],
      plan_started_at_in: ''
    }
  });

  const status = watch('status');
  const selectedTags = watch('tags');
  const planStartedAtIn = watch('plan_started_at_in');

  useEffect(() => {
    resetPage();
  }, [status, selectedTags, planStartedAtIn, resetPage]);

  const businessQueryParams = useMemo(() => {
    const filter: Record<string, unknown> = {
      name_or_email_cont: searchText,
      account_status_eq: status
    };

    if (selectedTags?.length) {
      filter.tags_name_in = selectedTags;
    }

    if (planStartedAtIn) {
      filter.plan_started_at_in = planStartedAtIn;
    }

    return {
      page: currentPage,
      per_page: perPage,
      include: ['business_plan', 'locations', 'business_addons'],
      filter
    };
  }, [currentPage, perPage, searchText, status, selectedTags, planStartedAtIn]);

  const { data: businesses, isLoading } = useGetBusinesses(businessQueryParams);

  const handleViewModeChange = (mode: BusinessesViewMode) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (mode === 'insights') next.set('view', 'insights');
        else next.delete('view');
        return next;
      },
      { replace: true }
    );
  };

  const handleSearchChange = (value: string | null) => {
    if (value !== searchText) {
      resetPage();
    }
    setSearchText(value);
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', pb: { xs: 28, sm: 0 } }}>
      <BusinessesHeader
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        searchText={searchText}
        onSearchChange={handleSearchChange}
        control={control}
      />
      {viewMode === 'list' ? (
        <BusinessesListing
          businesses={businesses?.data}
          isLoading={isLoading}
          totalPages={businesses?.meta?.total_pages}
          totalCount={businesses?.meta?.total_count ?? 0}
          currentPage={currentPage}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          onAddTags={(business) => setTagsBusiness(business)}
          onViewAddons={(business) => setAddonsBusiness(business)}
        />
      ) : (
        <BusinessesInsights />
      )}
      <AddBusinessTagsDrawer
        open={Boolean(tagsBusiness)}
        business={tagsBusiness}
        onClose={() => setTagsBusiness(null)}
      />
      <BusinessAddonsDrawer
        open={Boolean(addonsBusiness)}
        business={addonsBusiness}
        onClose={() => setAddonsBusiness(null)}
      />
    </Box>
  );
}
